import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send, X, UserPlus, AtSign, Users, Search as SearchIcon, Trash2, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { INSTRUMENTS, EVENT_TYPES } from "../lib/instruments";
import { useAuth } from "../context/AuthContext";
import { localYmd } from "../lib/utils";
import { toast } from "sonner";

const CROSS_INSTRUMENT_ROLES = new Set(["booker", "conductor", "admin"]);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function DepBroadcast() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCross = CROSS_INSTRUMENT_ROLES.has((user?.role || "").toLowerCase());

  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    instrument: "",
    event_type: "rehearsal",
    date: localYmd(new Date()),
    call_time: "",
    venue: "",
    notes: "",
  });

  // Recipient state
  const [tab, setTab] = useState("amds");           // amds | contacts | paste
  const [amdsResults, setAmdsResults] = useState([]);
  const [amdsLoading, setAmdsLoading] = useState(false);
  const [amdsQuery, setAmdsQuery] = useState("");
  const [selectedAmds, setSelectedAmds] = useState({});   // {user_id: {name, instrument}}
  const [contactList, setContactList] = useState([]);
  const [contactQuery, setContactQuery] = useState("");
  const [selectedContacts, setSelectedContacts] = useState({});
  const [pasteEmails, setPasteEmails] = useState("");
  const [freeformEmails, setFreeformEmails] = useState([]);

  const [submitting, setSubmitting] = useState(false);

  // ----- Profile (for instrument lock-in) -----
  useEffect(() => {
    if (isCross) return;
    let cancelled = false;
    api.get("/profile/me")
      .then(({ data }) => { if (!cancelled) setProfile(data || {}); })
      .catch(() => { if (!cancelled) setProfile({}); });
    return () => { cancelled = true; };
  }, [isCross]);

  const allowedInstruments = useMemo(() => {
    if (isCross) return INSTRUMENTS;
    const list = [
      (profile?.instrument || "").trim(),
      ...(profile?.secondary_instruments || []).filter(Boolean),
    ].filter(Boolean);
    const seen = new Set();
    return list.filter((i) => { const k = i.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
  }, [isCross, profile]);

  // Default instrument once we know it
  useEffect(() => {
    if (form.instrument) return;
    if (allowedInstruments.length > 0) setForm((p) => ({ ...p, instrument: allowedInstruments[0] }));
  }, [allowedInstruments, form.instrument]);

  // ----- AMDS user picker -----
  useEffect(() => {
    if (tab !== "amds") return;
    if (!form.instrument) { setAmdsResults([]); return; }
    let cancelled = false;
    setAmdsLoading(true);
    const params = new URLSearchParams({ instrument: form.instrument });
    api.get(`/search/musicians?${params.toString()}`)
      .then(({ data }) => {
        if (cancelled) return;
        const q = amdsQuery.trim().toLowerCase();
        let list = data || [];
        if (q) list = list.filter((p) =>
          (p.name || "").toLowerCase().includes(q) ||
          (p.location || "").toLowerCase().includes(q)
        );
        setAmdsResults(list);
      })
      .catch(() => { if (!cancelled) setAmdsResults([]); })
      .finally(() => { if (!cancelled) setAmdsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, form.instrument, amdsQuery]);

  // ----- Contacts picker -----
  useEffect(() => {
    if (tab !== "contacts") return;
    let cancelled = false;
    const params = new URLSearchParams();
    if (contactQuery.trim()) params.set("q", contactQuery.trim());
    api.get(`/contacts${params.toString() ? `?${params.toString()}` : ""}`)
      .then(({ data }) => { if (!cancelled) setContactList(data || []); })
      .catch(() => { if (!cancelled) setContactList([]); });
    return () => { cancelled = true; };
  }, [tab, contactQuery]);

  // ----- Paste-email parser -----
  const parsePaste = () => {
    const parts = pasteEmails
      .split(/[\s,;\n]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const valid = [];
    const invalid = [];
    const existing = new Set(freeformEmails);
    parts.forEach((e) => {
      if (!EMAIL_RE.test(e)) invalid.push(e);
      else if (!existing.has(e)) { valid.push(e); existing.add(e); }
    });
    if (invalid.length) toast.error(`Skipped ${invalid.length} invalid email${invalid.length > 1 ? "s" : ""}`);
    if (!valid.length) return;
    setFreeformEmails((prev) => [...prev, ...valid]);
    setPasteEmails("");
    toast.success(`Added ${valid.length} email${valid.length > 1 ? "s" : ""}`);
  };

  const removeFreeform = (em) => setFreeformEmails((prev) => prev.filter((x) => x !== em));

  const toggleAmds = (p) => {
    setSelectedAmds((prev) => {
      const next = { ...prev };
      if (next[p.user_id]) delete next[p.user_id];
      else next[p.user_id] = { name: p.name, instrument: p.instrument };
      return next;
    });
  };
  const toggleContact = (c) => {
    setSelectedContacts((prev) => {
      const next = { ...prev };
      if (next[c.id]) delete next[c.id];
      else next[c.id] = { name: c.name, email: c.email };
      return next;
    });
  };

  const totalRecipients =
    Object.keys(selectedAmds).length + Object.keys(selectedContacts).length + freeformEmails.length;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.instrument) { toast.error("Pick an instrument"); return; }
    if (!form.date) { toast.error("Pick a date"); return; }
    if (totalRecipients === 0) { toast.error("Add at least one recipient"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/deps/broadcast", {
        instrument: form.instrument,
        event_type: form.event_type,
        date: form.date,
        call_time: form.call_time || null,
        venue: form.venue || null,
        notes: form.notes || null,
        amds_user_ids: Object.keys(selectedAmds),
        contact_ids: Object.keys(selectedContacts),
        freeform_emails: freeformEmails,
      });
      const msg = data.failed
        ? `Sent ${data.sent} · ${data.failed} failed`
        : `Sent to ${data.sent} recipient${data.sent === 1 ? "" : "s"}`;
      toast.success(msg);
      navigate(`/deps/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.message) toast.error(detail.message);
      else toast.error(formatApiErrorDetail(detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-5" data-testid="dep-broadcast-page">
      <div>
        <div className="label-overline">Dep broadcast</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Send a dep request</h1>
        <p className="text-ink-600 mt-1 text-sm">
          One branded email — to AMDS players, your contacts, or any address. Replies come straight back to your inbox.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        {/* Details */}
        <section className="card-base p-4 sm:p-5 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="label-overline block mb-2 flex items-center gap-1.5">
                Instrument {!isCross && allowedInstruments.length <= 1 && <Lock size={11} className="text-ink-500" />}
              </label>
              <select className="input-field" value={form.instrument}
                onChange={(e) => setForm({ ...form, instrument: e.target.value })}
                disabled={!isCross && allowedInstruments.length <= 1}
                data-testid="dep-instrument-select">
                {(isCross ? INSTRUMENTS : allowedInstruments).map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
                {allowedInstruments.length === 0 && !isCross && (
                  <option value="">— set instrument on profile —</option>
                )}
              </select>
            </div>
            <div>
              <label className="label-overline block mb-2">Type</label>
              <select className="input-field" value={form.event_type}
                onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                data-testid="dep-event-type">
                {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-overline block mb-2">Date</label>
              <input type="date" required min={new Date().toISOString().slice(0, 10)} className="input-field" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="dep-date-input" />
            </div>
            <div>
              <label className="label-overline block mb-2">Call time</label>
              <input type="time" className="input-field" value={form.call_time}
                onChange={(e) => setForm({ ...form, call_time: e.target.value })}
                data-testid="dep-call-time-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-overline block mb-2">Venue</label>
              <input className="input-field" placeholder="Holywell Music Room, Oxford"
                value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })}
                data-testid="dep-venue-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-overline block mb-2">Notes <span className="text-ink-400 normal-case">(optional)</span></label>
              <textarea rows={3} className="input-field" placeholder="Programme, dress code, fee, anything useful…"
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="dep-notes-input" />
            </div>
          </div>
        </section>

        {/* Recipients */}
        <section className="card-base p-4 sm:p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="label-overline">Recipients</div>
              <h2 className="font-heading text-lg font-semibold mt-0.5">Who should hear about this?</h2>
            </div>
            <div className="pill bg-sage-100 text-sage-700" data-testid="recipient-count">
              {totalRecipients} selected
            </div>
          </div>

          <div className="flex gap-1 p-1 bg-ivory-100 rounded-full w-fit" data-testid="recipient-tabs">
            {[
              { v: "amds", label: "AMDS users", icon: Users },
              { v: "contacts", label: "My contacts", icon: UserPlus },
              { v: "paste", label: "Paste emails", icon: AtSign },
            ].map((t) => (
              <button type="button" key={t.v} onClick={() => setTab(t.v)}
                data-testid={`recipient-tab-${t.v}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition ${
                  tab === t.v ? "bg-sage-500 text-white shadow-soft" : "text-ink-700 hover:bg-white/70"
                }`}>
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>

          {/* AMDS users */}
          {tab === "amds" && (
            <div className="space-y-2">
              <div className="relative">
                <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                <input className="input-field pl-9" placeholder="Filter by name or location"
                  value={amdsQuery} onChange={(e) => setAmdsQuery(e.target.value)}
                  data-testid="amds-search-input" />
              </div>
              <div className="max-h-72 overflow-y-auto rounded-xl border border-ivory-200 divide-y" data-testid="amds-results">
                {amdsLoading && <div className="p-3 text-sm text-ink-600">Loading…</div>}
                {!amdsLoading && amdsResults.length === 0 && (
                  <div className="p-3 text-sm text-ink-600">No matching AMDS users for this instrument.</div>
                )}
                {!amdsLoading && amdsResults.map((p) => {
                  const on = !!selectedAmds[p.user_id];
                  return (
                    <button type="button" key={p.user_id} onClick={() => toggleAmds(p)}
                      data-testid={`amds-pick-${p.user_id}`}
                      className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 hover:bg-ivory-100 ${on ? "bg-sage-50" : ""}`}>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{p.name}</div>
                        <div className="text-xs text-ink-500 truncate">{p.instrument || "—"}{p.location ? ` · ${p.location}` : ""}</div>
                      </div>
                      <input type="checkbox" readOnly checked={on}
                        className="w-5 h-5 accent-sage-500 shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Contacts */}
          {tab === "contacts" && (
            <div className="space-y-2">
              <div className="relative">
                <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                <input className="input-field pl-9" placeholder="Filter by name, email or instrument"
                  value={contactQuery} onChange={(e) => setContactQuery(e.target.value)}
                  data-testid="contacts-search-input" />
              </div>
              <div className="max-h-72 overflow-y-auto rounded-xl border border-ivory-200 divide-y" data-testid="contacts-results">
                {contactList.length === 0 && (
                  <div className="p-3 text-sm text-ink-600">
                    No contacts yet. <Link to="/contacts" className="text-sage-600 underline">Add one</Link>.
                  </div>
                )}
                {contactList.map((c) => {
                  const on = !!selectedContacts[c.id];
                  return (
                    <button type="button" key={c.id} onClick={() => toggleContact(c)}
                      data-testid={`contact-pick-${c.id}`}
                      className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 hover:bg-ivory-100 ${on ? "bg-sage-50" : ""}`}>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{c.name}</div>
                        <div className="text-xs text-ink-500 truncate">{c.email}{c.instrument ? ` · ${c.instrument}` : ""}</div>
                      </div>
                      <input type="checkbox" readOnly checked={on}
                        className="w-5 h-5 accent-sage-500 shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Paste */}
          {tab === "paste" && (
            <div className="space-y-2">
              <textarea rows={3} className="input-field" placeholder="paste@example.com, another@example.com"
                value={pasteEmails} onChange={(e) => setPasteEmails(e.target.value)}
                data-testid="paste-input" />
              <div className="flex justify-end">
                <button type="button" onClick={parsePaste} disabled={!pasteEmails.trim()}
                  className="btn-secondary text-sm" data-testid="paste-add-btn">Add emails</button>
              </div>
              {freeformEmails.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="paste-chips">
                  {freeformEmails.map((em) => (
                    <span key={em} className="pill bg-ivory-100 text-ink-700 gap-1.5">
                      {em}
                      <button type="button" onClick={() => removeFreeform(em)}
                        className="text-ink-500 hover:text-terracotta-500"
                        data-testid={`paste-remove-${em}`}><X size={12} /></button>
                    </span>
                  ))}
                  <button type="button" onClick={() => setFreeformEmails([])}
                    className="pill bg-terracotta-50 text-terracotta-700">
                    <Trash2 size={12} /> Clear all
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={() => navigate("/deps")} className="btn-ghost" data-testid="dep-cancel-btn">Cancel</button>
          <button type="submit" disabled={submitting || totalRecipients === 0}
            className="btn-primary" data-testid="dep-send-btn">
            <Send size={16} /> {submitting ? "Sending…" : `Send to ${totalRecipients || ""} ${totalRecipients === 1 ? "person" : "people"}`}
          </button>
        </div>
      </form>
    </div>
  );
}
